import { createContext, useContext, useEffect, useReducer } from 'react';
import { authService } from '../services/auth.service';
import { User, AuthState, RegisterParams } from '../types/auth.types';

// État initial de l'authentification
const initialAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
  isInitialized: false,
  isNewUser: false
};

// Types d'actions pour le réducteur
type AuthAction =
  | { type: 'INITIALIZE'; payload: { isAuthenticated: boolean; user: User | null } }
  | { type: 'LOGIN'; payload: { user: User } }
  | { type: 'REGISTER'; payload: { user: User, isNewUser: boolean } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: { user: User } }
  | { type: 'AUTH_ERROR'; payload: { error: Error } }
  | { type: 'RESET_ERROR' };

// Fonction réducteur pour gérer les états d'authentification
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        user: action.payload.user,
        isLoading: false,
        isInitialized: true
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isLoading: false,
        error: null
      };
    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isLoading: false,
        error: null,
        isNewUser: action.payload.isNewUser
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload.user
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        error: action.payload.error,
        isLoading: false
      };
    case 'RESET_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Interface du contexte d'authentification
interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (params: RegisterParams) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  updateUserLocation: () => Promise<void>;
  resetAuthError: () => void;
}

// Création du contexte d'authentification
const AuthContext = createContext<AuthContextValue | null>(null);

// Fournisseur du contexte d'authentification
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);

  // Fonction pour mettre à jour la position de l'utilisateur
  const updateUserLocation = async (): Promise<void> => {
    if (!navigator.geolocation) return;
    
    try {
      navigator.geolocation.getCurrentPosition(async ({ coords }) => {
        if (!state.user) return;
        
        try {
          const updatedUser = await authService.updateUserLocation({
            latitude: coords.latitude,
            longitude: coords.longitude
          });
          
          dispatch({
            type: 'UPDATE_USER',
            payload: { user: updatedUser }
          });
          
          console.log('Position mise à jour avec succès');
        } catch (error) {
          console.error('Erreur lors de la mise à jour de la position:', error);
        }
      });
    } catch (error) {
      console.error('Erreur lors de l\'accès à la géolocalisation:', error);
    }
  };

  // Fonction pour gérer la connexion avec email/mot de passe
  const login = async (email: string, password: string): Promise<void> => {
    try {
      dispatch({ type: 'RESET_ERROR' });
      const user = await authService.loginWithEmailPassword(email, password);
      
      dispatch({
        type: 'LOGIN',
        payload: { user }
      });
      
      // Mise à jour automatique de la position de l'utilisateur après connexion
      await updateUserLocation();
    } catch (error: any) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: { error }
      });
      throw error;
    }
  };

  // Fonction pour gérer la connexion avec Google
  const loginWithGoogle = async (): Promise<void> => {
    try {
      dispatch({ type: 'RESET_ERROR' });
      await authService.loginWithGoogle();
      // Notez que la redirection aura lieu ici, donc le code après cette ligne ne s'exécutera pas
    } catch (error: any) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: { error }
      });
      throw error;
    }
  };

  // Fonction pour gérer l'inscription
  const register = async (params: RegisterParams): Promise<void> => {
    try {
      dispatch({ type: 'RESET_ERROR' });
      const user = await authService.registerWithEmailPassword(params);
      
      dispatch({
        type: 'REGISTER',
        payload: {
          user,
          isNewUser: true
        }
      });
      
      // Mise à jour automatique de la position de l'utilisateur après inscription
      await updateUserLocation();
    } catch (error: any) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: { error }
      });
      throw error;
    }
  };

  // Fonction pour gérer la déconnexion
  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      dispatch({ type: 'LOGOUT' });
    } catch (error: any) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: { error }
      });
      throw error;
    }
  };

  // Fonction pour mettre à jour les données utilisateur
  const updateUser = async (userData: Partial<User>): Promise<void> => {
    try {
      const updatedUser = await authService.updateUser(userData);
      
      dispatch({
        type: 'UPDATE_USER',
        payload: { user: updatedUser }
      });
    } catch (error: any) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: { error }
      });
      throw error;
    }
  };

  // Fonction pour réinitialiser les erreurs d'authentification
  const resetAuthError = (): void => {
    dispatch({ type: 'RESET_ERROR' });
  };

  // Effet pour initialiser le système d'authentification et écouter les changements
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Vérifie d'abord s'il y a un résultat de redirection (connexion Google)
        const redirectUser = await authService.getRedirectResultHandler();
        
        if (redirectUser) {
          dispatch({
            type: 'LOGIN',
            payload: { user: redirectUser }
          });
          
          // Mise à jour automatique de la position de l'utilisateur
          updateUserLocation();
          return;
        }
        
        // Sinon, écouter les changements d'état d'authentification
        const unsubscribe = authService.onAuthStateChanged(async (firebaseUser: any) => {
          if (firebaseUser) {
            try {
              // Utilisateur connecté, récupérer les données depuis notre API
              const user = await authService.getCurrentUser();
              
              dispatch({
                type: 'INITIALIZE',
                payload: {
                  isAuthenticated: true,
                  user
                }
              });
              
              // Mise à jour automatique de la position de l'utilisateur
              updateUserLocation();
            } catch (error) {
              console.error('Erreur lors de la récupération des données utilisateur:', error);
              dispatch({
                type: 'INITIALIZE',
                payload: {
                  isAuthenticated: false,
                  user: null
                }
              });
            }
          } else {
            // Aucun utilisateur connecté
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: false,
                user: null
              }
            });
          }
        });
        
        // Nettoyer l'écouteur lors du démontage du composant
        return () => unsubscribe();
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de l\'authentification:', error);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };
    
    initializeAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        loginWithGoogle,
        register,
        logout,
        updateUser,
        updateUserLocation,
        resetAuthError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook personnalisé pour accéder au contexte d'authentification
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  
  return context;
};
